import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, X } from 'lucide-react';

/**
 * Banner exibido quando o Service Worker detecta uma nova versão da aplicação.
 * Escuta o evento global "vital:update-available" disparado por index.js.
 */
export default function UpdateBanner() {
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [visible, setVisible] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      setWaitingWorker(e.detail?.worker || null);
      setVisible(true);
    };
    window.addEventListener('vital:update-available', handler);
    return () => window.removeEventListener('vital:update-available', handler);
  }, []);

  const handleUpdate = useCallback(() => {
    setUpdating(true);
    // Tell the waiting SW to activate; index.js listens for controllerchange and reloads.
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    } else {
      window.location.reload();
    }
  }, [waitingWorker]);

  const handleDismiss = useCallback(() => {
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      data-testid="update-banner"
      className="fixed inset-x-0 bottom-0 z-[9999] px-3 pb-3 sm:bottom-4 sm:right-4 sm:left-auto sm:px-0 sm:pb-0 animate-in slide-in-from-bottom-4 duration-300"
    >
      <div className="mx-auto sm:mx-0 max-w-md rounded-xl border border-slate-700/60 bg-slate-900/95 backdrop-blur-md shadow-2xl shadow-black/40 ring-1 ring-white/5">
        <div className="flex items-start gap-3 p-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-500/15 ring-1 ring-blue-400/30">
            <RefreshCw className="h-5 w-5 text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white">Nova versão disponível</p>
            <p className="mt-0.5 text-xs text-slate-400 leading-relaxed">
              Atualize agora para receber as últimas melhorias e correções.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <button
                data-testid="update-banner-apply-btn"
                onClick={handleUpdate}
                disabled={updating}
                className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:bg-blue-700 disabled:opacity-70 px-3 py-1.5 text-xs font-medium text-white transition-colors"
              >
                {updating ? (
                  <>
                    <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                    Atualizando...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-3.5 w-3.5" />
                    Atualizar agora
                  </>
                )}
              </button>
              <button
                data-testid="update-banner-dismiss-btn"
                onClick={handleDismiss}
                disabled={updating}
                className="text-xs font-medium text-slate-400 hover:text-slate-200 px-2 py-1.5 transition-colors"
              >
                Depois
              </button>
            </div>
          </div>
          <button
            data-testid="update-banner-close-btn"
            onClick={handleDismiss}
            disabled={updating}
            aria-label="Fechar"
            className="shrink-0 text-slate-500 hover:text-slate-300 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
